import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './components/SEO';
import { ThemeContext } from './components/Core/ThemeContext';
import { perspectives } from './data/perspectives';
import { assignDependencyLayers, balanceDependencyLayers, buildPerspectiveFoundationGraph, buildPredicateDependencyLayer, buildPredicateThemeLayer, buildCreedalFoundationLayer, CREEDAL_FIRST_PRINCIPLE_LINKS, orderDependencyLayer, traceDependencyRoutes } from './data/perspectiveFoundationGraph';
import './perspectives.css';

const NODE_WIDTH = 326;
const NODE_HEIGHT = 66;
const COLUMN_GAP = 116;
const ROW_GAP = 34;
const MAX_DEPTH = 4;
const MAX_NODES = 48;

// The 100-topic expansion deliberately reuses three broad reading templates.
// These chart-only foundations make the reasoning behind those shared lenses
// visible without pretending they were authored as topic-specific claims.
const RADIAL_FIRST_PRINCIPLES = [
    {
        id: 'radial-foundation-creation-and-life',
        label: 'God is creator and source of life',
        subtitle: 'First principle · creation',
        explanation: 'This map begins from a theistic Christian premise: life and creation depend on God. It is an editorial starting point for the generated browsing branches, not a claim that every perspective explicitly argues from this premise.',
        references: 'Genesis 1:1; Acts 17:24–28',
    },
    {
        id: 'radial-foundation-divine-revelation',
        label: 'God can make truth known',
        subtitle: 'First principle · revelation',
        explanation: 'Christian theological reasoning assumes that God is not wholly unknowable and can communicate truth. Traditions differ over the sources, authority, and interpretation of revelation.',
        references: 'Hebrews 1:1–3; Romans 1:19–20',
    },
    {
        id: 'radial-foundation-divine-goodness',
        label: 'God is good and calls creation toward good',
        subtitle: 'First principle · goodness',
        explanation: 'The map treats God’s goodness as a broad Christian commitment that grounds moral reflection, while acknowledging that Christians disagree about how particular moral duties follow from it.',
        references: 'Psalm 119:68; Mark 10:18',
    },
    {
        id: 'radial-foundation-divine-identity',
        label: 'God is one; divine personhood and agency need careful distinction',
        subtitle: 'First principle · divine identity',
        explanation: 'Christian perspectives confess one God while differing over how divine unity, personhood, and agency are described. This editorial starting point makes that live question visible; it is not a creed-level conclusion or a claim that either creed settles every later formulation.',
        references: 'Deuteronomy 6:4; Mark 12:29',
    },
];

const RADIAL_SHARED_FOUNDATIONS = [
    {
        id: 'radial-foundation-scripture-revelation',
        label: 'God communicates truth through Scripture',
        subtitle: 'Theological foundation · Scripture',
        dependsOn: ['radial-foundation-divine-revelation'],
        explanation: 'A broad Christian starting point for approaches that treat biblical teaching as a source of theological or moral guidance. The chart uses 2 Timothy 3:16 as a reference point; traditions differ on canon, authority, and interpretation.',
        references: '2 Timothy 3:16; Luke 24:27',
    },
    {
        id: 'radial-foundation-image-bearing-dignity',
        label: 'People bear God’s image and have inherent dignity',
        subtitle: 'Theological foundation · human dignity',
        dependsOn: ['radial-foundation-creation-and-life'],
        explanation: 'A biblical foundation commonly used in Christian reasoning about human dignity and care. Genesis 1:26–27 and James 3:9–10 connect human beings with the image of God; the chart’s grouping is an editorial synthesis, not a claim that every perspective makes this argument explicitly.',
        references: 'Genesis 1:26–27; James 3:9–10',
    },
    {
        id: 'radial-foundation-love-of-god-neighbour',
        label: 'Love of God and neighbour orders faithful life',
        subtitle: 'Theological foundation · love and ethics',
        dependsOn: ['radial-foundation-divine-goodness', 'radial-foundation-divine-revelation'],
        explanation: 'Jesus names love of God and neighbour as the two great commandments. This provides a broad ethical root for pastoral and social-care reasoning, while leaving debates about application open.',
        references: 'Mark 12:29–31; Matthew 22:37–40',
    },
];

const RADIAL_TEMPLATE_FOUNDATIONS = [
    {
        id: 'radial-foundation-scriptural-boundary',
        templateId: 'principled-boundary',
        label: 'Direct teaching and received norms set boundaries',
        subtitle: 'Interpretive foundation · clear-boundary approach',
        dependsOn: ['radial-foundation-scripture-revelation'],
        explanation: 'This branch groups readings whose shared template gives priority to a direct command, doctrinal principle, or inherited norm. It is a browsing scaffold inferred from the template wording, not a separately recorded predicate on each perspective.',
    },
    {
        id: 'radial-foundation-contextual-reading',
        templateId: 'contextual-discernment',
        label: 'Read texts in context and across the wider witness',
        subtitle: 'Interpretive foundation · contextual approach',
        dependsOn: ['radial-foundation-scripture-revelation'],
        explanation: 'This branch groups readings whose shared template stresses literary and historical context, competing biblical themes, conscience, and practical effects. It is a browsing scaffold inferred from the template wording, not a separately recorded predicate on each perspective.',
    },
    {
        id: 'radial-foundation-neighbour-care',
        templateId: 'pastoral-and-social-care',
        label: 'Attend to dignity, safety, justice, and neighbour-care',
        subtitle: 'Interpretive foundation · pastoral/social approach',
        dependsOn: ['radial-foundation-image-bearing-dignity', 'radial-foundation-love-of-god-neighbour'],
        explanation: 'This branch groups readings whose shared template foregrounds the dignity and safety of people affected, and asks how truth, mercy, justice, and practical care belong together. It is a browsing scaffold inferred from the template wording, not a separately recorded predicate on each perspective.',
    },
];

const buildNetwork = () => {
    const foundationGraph = buildPerspectiveFoundationGraph(perspectives);
    const nodes = new Map(foundationGraph.nodes.map((node) => [node.id, {
        ...node,
        type: 'perspective',
        subtitle: node.category,
    }]));
    const edges = [];
    const addClaim = (source, claim, target = null) => {
        if (!nodes.has(claim.id)) {
            nodes.set(claim.id, {
                id: claim.id,
                type: 'predicate',
                label: claim.label,
                subtitle: 'Interpretive commitment',
                foundationLayer: claim.layer,
                relationType: claim.relationType,
                targetId: target,
            });
        }
        edges.push({ source, target: claim.id, label: claim.relationType.replace(/-/g, ' ') });
        if (target) edges.push({ source: claim.id, target, label: 'grounds' });
    };
    foundationGraph.foundationEdges.forEach((edge) => addClaim(edge.source, edge.claim, edge.target));
    foundationGraph.unlinkedClaims.forEach((item) => addClaim(item.source, item.claim));
    foundationGraph.perspectiveRelations.forEach((relation) => {
        if (relation.target) edges.push({ source: relation.source, target: relation.target, label: relation.type });
    });
    return { nodes, edges, foundationGraph };
};

const network = buildNetwork();
const foundationalPredicates = Array.from(network.nodes.values())
    .filter((node) => node.type === 'predicate' && network.edges.some((edge) => edge.source === node.id && edge.label === 'grounds'))
    .sort((a, b) => a.label.localeCompare(b.label));

const allPerspectiveNodes = Array.from(network.nodes.values())
    .filter((node) => node.type === 'perspective')
    .sort((a, b) => a.label.localeCompare(b.label));
const allPredicateNodes = Array.from(network.nodes.values())
    .filter((node) => node.type === 'predicate')
    .sort((a, b) => a.label.localeCompare(b.label));
const allPerspectiveBadges = Array.from(new Set(allPerspectiveNodes.flatMap((node) => node.reading.badges || [])))
    .sort((a, b) => a.localeCompare(b));

const buildRadialTree = (selectedBadges, searchText = '') => {
    const graph = network.foundationGraph;
    const sharedFoundations = RADIAL_SHARED_FOUNDATIONS.map((foundation) => ({
        ...foundation,
        type: 'foundation',
        category: 'Theological foundations',
        reading: { badges: [] },
        href: null,
    }));
    const firstPrinciples = RADIAL_FIRST_PRINCIPLES.map((foundation) => ({
        ...foundation,
        type: 'foundation',
        category: 'First principles',
        reading: { badges: [] },
        href: null,
    }));
    const templateFoundations = RADIAL_TEMPLATE_FOUNDATIONS.map((foundation) => ({
        ...foundation,
        type: 'foundation',
        category: 'Interpretive foundations',
        reading: { badges: [] },
        href: null,
    }));
    const templateFoundationById = new Map(templateFoundations.map((node) => [node.templateId, node]));
    const templateEdges = graph.nodes
        .filter((node) => templateFoundationById.has(node.readingId) && !(node.reading.predicates || []).length)
        .map((node) => {
            const foundation = templateFoundationById.get(node.readingId);
            return {
                id: `${node.id}|editorial-template-foundation|${foundation.id}`,
                source: node.id,
                target: foundation.id,
                claim: {
                    id: foundation.id,
                    label: foundation.label,
                    layer: 'interpretive-principle',
                    relationType: 'depends-on',
                    argument: foundation.explanation,
                    linked: true,
                    editorial: true,
                },
            };
        });
    const { nodes: canonicalPredicateNodes, edges: canonicalPredicateEdges } = buildPredicateDependencyLayer(graph);
    const canonicalPredicateById = new Map(canonicalPredicateNodes.map((node) => [node.id, node]));
    const predicateFoundationByOccurrence = new Map(canonicalPredicateEdges
        .filter((edge) => edge.kind === 'grounds')
        .map((edge) => [edge.id.replace(/\|predicate-foundation$/, ''), edge]));
    const predicateNodes = [];
    const predicateEdges = [];
    canonicalPredicateEdges.filter((edge) => edge.kind === 'depends-on').forEach((edge) => {
        const occurrenceKey = edge.id.replace(/\|depends-on-predicate$/, '');
        const nodeId = `predicate-occurrence:${occurrenceKey}`;
        const canonicalNode = canonicalPredicateById.get(edge.target);
        const foundationEdge = predicateFoundationByOccurrence.get(occurrenceKey);
        if (!canonicalNode) return;
        predicateNodes.push({
            ...canonicalNode,
            id: nodeId,
            canonicalId: canonicalNode.id,
            ownerId: edge.source,
            targetId: foundationEdge?.target || null,
            targetIds: foundationEdge ? [foundationEdge.target] : [],
        });
        predicateEdges.push({ ...edge, target: nodeId });
        if (foundationEdge) predicateEdges.push({ ...foundationEdge, source: nodeId });
    });
    const sharedAndFirstById = new Map([...firstPrinciples, ...sharedFoundations].map((node) => [node.id, node]));
    const sharedFoundationEdges = sharedFoundations.flatMap((foundation) => foundation.dependsOn.map((foundationId) => {
        const parent = sharedAndFirstById.get(foundationId);
        return {
            id: `${foundation.id}|editorial-foundation|${foundationId}`,
            source: foundation.id,
            target: foundationId,
            claim: {
                id: foundationId,
                label: parent.label,
                layer: 'first-principle',
                relationType: 'depends-on',
                argument: parent.explanation,
                linked: true,
                editorial: true,
            },
        };
    }));
    const approachFoundationEdges = templateFoundations.flatMap((approach) => approach.dependsOn.map((foundationId) => {
        const foundation = sharedAndFirstById.get(foundationId);
        return {
            id: `${approach.id}|editorial-foundation|${foundationId}`,
            source: approach.id,
            target: foundationId,
            claim: {
                id: foundationId,
                label: foundation.label,
                layer: 'first-principle',
                relationType: 'depends-on',
                argument: foundation.explanation,
                linked: true,
                editorial: true,
            },
        };
    }));
    const { nodes: thematicFoundations, edges: thematicPredicateEdges } = buildPredicateThemeLayer(predicateNodes);
    const { nodes: creedalFoundations, themeEdges: thematicCreedEdges, predicateEdges: creedalPredicateEdges } = buildCreedalFoundationLayer(thematicFoundations, predicateNodes);
    const allFoundationById = new Map([...firstPrinciples, ...sharedFoundations, ...creedalFoundations].map((node) => [node.id, node]));
    const creedalPrincipleEdges = firstPrinciples.flatMap((principle) => (CREEDAL_FIRST_PRINCIPLE_LINKS[principle.id] || []).map((creedId) => {
        const creed = allFoundationById.get(creedId);
        return {
            id: `${principle.id}|editorial-creed|${creedId}`,
            source: principle.id,
            target: creedId,
            kind: 'depends-on',
            claim: { id: creedId, label: creed.label, relationType: 'depends-on', argument: 'Editorial placement immediately beyond the creedal root ring; a theological coherence link, not a predicate recorded by each perspective.', editorial: true, creedal: true },
        };
    }));
    const thematicFoundationEdges = thematicFoundations.flatMap((theme) => theme.dependsOn.map((foundationId) => {
        const foundation = allFoundationById.get(foundationId);
        return {
            id: `${theme.id}|editorial-foundation|${foundationId}`,
            source: theme.id,
            target: foundationId,
            kind: 'depends-on',
            claim: {
                id: foundationId,
                label: foundation.label,
                relationType: 'depends-on',
                argument: 'Editorial thematic bridge to a broader map foundation; it does not replace authored perspective predicates.',
                editorial: true,
            },
        };
    }));
    const thematicFoundationById = new Map(thematicFoundations.map((theme) => [theme.id, theme]));
    const themeLabelsByPredicateId = new Map();
    thematicPredicateEdges.forEach((edge) => {
        const label = thematicFoundationById.get(edge.target)?.label;
        if (!label) return;
        if (!themeLabelsByPredicateId.has(edge.source)) themeLabelsByPredicateId.set(edge.source, []);
        themeLabelsByPredicateId.get(edge.source).push(label);
    });
    const predicatesWithThemeContext = predicateNodes.map((node) => {
        const themeLabels = themeLabelsByPredicateId.get(node.id) || [];
        return themeLabels.length ? {
            ...node,
            subtitle: 'Recorded predicate · cross-cutting editorial routes',
            explanation: `No authored foundation reading is linked to this claim. Based on wording, the map connects it with these provisional themes: ${themeLabels.map((label) => `“${label}”`).join('; ')}. It may also link directly to a creed clause whose topic is named in the wording. These routes are browsing associations, not authored dependencies or claims that the perspective explicitly reasons from them.`,
        } : node;
    });
    const sharedTemplateIds = new Set(['principled-boundary', 'contextual-discernment', 'pastoral-and-social-care']);
    const topicsBySlug = new Map(perspectives.map((topic) => [topic.slug, topic]));
    const perspectiveNodes = graph.nodes.map((node) => {
        const topic = topicsBySlug.get(node.topicSlug);
        const isSharedTemplate = sharedTemplateIds.has(node.readingId);
        const topicTitle = topic?.shortTitle || topic?.question;
        return {
            ...node,
            type: 'perspective',
            label: isSharedTemplate && topicTitle ? topicTitle : node.label,
            subtitle: isSharedTemplate ? `${node.label} · ${node.category}` : node.category,
            radialTopicTitle: isSharedTemplate ? topicTitle : null,
            radialReadingTitle: isSharedTemplate ? node.label : null,
        };
    });
    const allNodes = [...perspectiveNodes, ...templateFoundations, ...thematicFoundations, ...creedalFoundations, ...sharedFoundations, ...firstPrinciples, ...predicatesWithThemeContext];
    const allEdges = [...predicateEdges, ...thematicPredicateEdges, ...thematicCreedEdges, ...creedalPredicateEdges, ...creedalPrincipleEdges, ...templateEdges, ...approachFoundationEdges, ...thematicFoundationEdges, ...sharedFoundationEdges];
    const byId = new Map(allNodes.map((node) => [node.id, node]));
    const badgeFilter = new Set(selectedBadges);
    const searchNeedle = searchText.trim().toLowerCase();
    const seeds = graph.nodes.filter((node) => {
        const badgeMatch = !badgeFilter.size || (node.reading.badges || []).some((badge) => badgeFilter.has(badge));
        const searchableText = [node.label, node.reading.title, node.reading.thesis, node.reading.summary, ...(node.reading.predicates || []).map((predicate) => predicate.label)].filter(Boolean).join(' ').toLowerCase();
        return badgeMatch && (!searchNeedle || searchableText.includes(searchNeedle));
    });
    const included = new Set(seeds.map((node) => node.id));
    const dependencies = new Map();
    allEdges.forEach((edge) => {
        if (!dependencies.has(edge.source)) dependencies.set(edge.source, []);
        dependencies.get(edge.source).push(edge.target);
    });
    const queue = [...included];
    while (queue.length) {
        (dependencies.get(queue.shift()) || []).forEach((target) => {
            if (!included.has(target)) { included.add(target); queue.push(target); }
        });
    }
    const edges = allEdges.filter((edge) => included.has(edge.source) && included.has(edge.target));
    const dependencyLayers = assignDependencyLayers([...included], edges);
    const balancedLayers = balanceDependencyLayers([...included], edges, dependencyLayers.depthById, 140);
    const depthById = new Map(balancedLayers.depthById);
    // Give each recorded predicate occurrence its own node. This lets every
    // reading keep its predicates exactly one ring inward, even when the same
    // canonical claim is used by readings at different dependency depths.
    predicateNodes.forEach((predicate) => {
        const ownerDepth = depthById.get(predicate.ownerId);
        if (ownerDepth !== undefined && ownerDepth > 0) depthById.set(predicate.id, ownerDepth - 1);
    });
    const maxDepth = Math.max(0, ...depthById.values());
    const layerNodes = Array.from({ length: maxDepth + 1 }, (_, depth) => [...included].filter((id) => depthById.get(id) === depth));
    // The centre is an explicit, shared starting premise for this Christian
    // perspectives map. Node depth follows predicate edges and separately
    // marked browsing scaffolds; hub-to-root spokes are not authored claims.
    const perspectiveLayers = layerNodes.reduce((result, ids, index) => {
        const minimumRadius = index ? result[index - 1].radius + 790 : 850;
        result.push({ index: index + 1, count: ids.length, radius: Math.max(minimumRadius, ids.length * 310 / (2 * Math.PI)) });
        return result;
    }, []);
    const layers = [{ index: 0, count: 1, radius: 0, type: 'starting-premise' }, ...perspectiveLayers];
    const center = (perspectiveLayers[perspectiveLayers.length - 1]?.radius || 850) + 520;
    const positionById = new Map();
    const angleById = new Map();
    const labelById = new Map([...included].map((id) => [id, byId.get(id)?.label || id]));
    layerNodes.forEach((ids, depth) => {
        const ordered = orderDependencyLayer(ids, edges, depthById, angleById, labelById);
        ordered.forEach((id, index) => {
            const angle = -Math.PI / 2 + index / Math.max(1, ordered.length) * Math.PI * 2;
            angleById.set(id, angle);
            positionById.set(id, { x: center + Math.cos(angle) * layers[depth + 1].radius, y: center + Math.sin(angle) * layers[depth + 1].radius });
        });
    });
    const nodes = [...included].map((id) => ({ node: byId.get(id), id, angle: angleById.get(id), layer: depthById.get(id) + 1, position: positionById.get(id) }));
    const totalPredicates = new Set(edges.filter((edge) => edge.kind === 'depends-on' && !edge.claim.editorial).map((edge) => edge.claim.id)).size;
    return { nodes, edges, layers, width: center * 2, height: center * 2, center, perspectiveCount: [...included].filter((id) => byId.get(id)?.type === 'perspective').length, selectedCount: seeds.length, predicateCount: totalPredicates, predicateNodeCount: [...included].filter((id) => byId.get(id)?.type === 'predicate').length, recordedPredicateCount: [...included].filter((id) => byId.get(id)?.type === 'predicate' && !byId.get(id)?.targetId).length, totalLeaves: edges.length };
};

const buildBranch = (rootId) => {
    const outgoing = new Map();
    network.edges.forEach((edge) => {
        if (!outgoing.has(edge.source)) outgoing.set(edge.source, []);
        outgoing.get(edge.source).push(edge);
    });

    const levels = new Map([[rootId, 0]]);
    const treeEdges = [];
    const queue = [rootId];
    while (queue.length && levels.size < MAX_NODES) {
        const source = queue.shift();
        const level = levels.get(source);
        if (level >= MAX_DEPTH) continue;
        const candidates = (outgoing.get(source) || []).slice().sort((a, b) => {
            const aPredicate = network.nodes.get(a.target)?.type === 'predicate' ? 0 : 1;
            const bPredicate = network.nodes.get(b.target)?.type === 'predicate' ? 0 : 1;
            return aPredicate - bPredicate;
        });
        candidates.forEach((edge) => {
            if (levels.size >= MAX_NODES || levels.has(edge.target)) return;
            levels.set(edge.target, level + 1);
            treeEdges.push(edge);
            queue.push(edge.target);
        });
    }

    const children = new Map();
    treeEdges.forEach((edge) => {
        if (!children.has(edge.source)) children.set(edge.source, []);
        children.get(edge.source).push(edge.target);
    });

    const positions = new Map();
    let nextLeaf = 0;
    const place = (id) => {
        const childIds = children.get(id) || [];
        let y;
        if (!childIds.length) {
            y = nextLeaf * (NODE_HEIGHT + ROW_GAP);
            nextLeaf += 1;
        } else {
            childIds.forEach(place);
            y = childIds.reduce((sum, childId) => sum + positions.get(childId).y, 0) / childIds.length;
        }
        positions.set(id, {
            x: 28 + levels.get(id) * (NODE_WIDTH + COLUMN_GAP),
            y: 30 + y,
        });
    };
    place(rootId);

    const visible = new Set(levels.keys());
    const crossEdges = network.edges.filter((edge) =>
        visible.has(edge.source)
        && visible.has(edge.target)
        && !treeEdges.some((treeEdge) => treeEdge.source === edge.source && treeEdge.target === edge.target && treeEdge.label === edge.label)
    );
    const nodes = Array.from(visible).map((id) => network.nodes.get(id));
    const width = Math.max(920, (Math.max(...levels.values()) + 1) * (NODE_WIDTH + COLUMN_GAP) + 40);
    const height = Math.max(330, nextLeaf * (NODE_HEIGHT + ROW_GAP) + 60);
    return { nodes, edges: [...treeEdges, ...crossEdges], treeEdges, positions, width, height };
};

const truncate = (text, length) => {
    const value = typeof text === 'string' ? text : text == null ? '' : String(text);
    return value.length > length ? `${value.slice(0, length - 1)}…` : value;
};
const wrapSvgLabel = (text, maxChars = 29, maxLines = 2) => {
    const words = truncate(text, Number.MAX_SAFE_INTEGER).trim().split(/\s+/).filter(Boolean);
    if (!words.length) return ['Untitled'];
    const lines = [];
    let line = '';
    words.forEach((word) => {
        const next = line ? `${line} ${word}` : word;
        if (next.length > maxChars && line && lines.length < maxLines - 1) {
            lines.push(line);
            line = word;
        } else {
            line = next;
        }
    });
    if (line) lines.push(line);
    if (lines.length > maxLines) lines.length = maxLines;
    if (words.join(' ').length > lines.join(' ').length && lines.length) lines[lines.length - 1] = truncate(lines[lines.length - 1], maxChars);
    return lines;
};

function PagePerspectiveChart() {
    const theme = useContext(ThemeContext);
    const [rootId, setRootId] = useState(foundationalPredicates[0]?.id);
    const [viewMode, setViewMode] = useState('radial');
    const [radialBadges, setRadialBadges] = useState([]);
    const [radialSearch, setRadialSearch] = useState('');
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [radialSelection, setRadialSelection] = useState(null);
    const [badgeQuery, setBadgeQuery] = useState('');
    const [radialZoom, setRadialZoom] = useState(.1);
    const [query, setQuery] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [zoom, setZoom] = useState(0.92);
    const [isPanning, setIsPanning] = useState(false);
    const chartViewportRef = useRef(null);
    const radialViewportRef = useRef(null);
    const panRef = useRef(null);
    const radialGestureRef = useRef({ pointers: new Map(), pinch: null });

    useEffect(() => {
        window.scrollTo(0, 0);
        theme.loading(false);
    }, [theme]);

    const branch = useMemo(() => buildBranch(rootId), [rootId]);
    const radialTree = useMemo(() => buildRadialTree(radialBadges, radialSearch), [radialBadges, radialSearch]);
    const selectedRadialNode = radialSelection
        ? radialTree.nodes.find((item) => item.id === radialSelection.id)?.node
        : null;
    const selectedRadialPerspective = radialSelection?.type === 'perspective'
        ? selectedRadialNode
        : null;
    const selectedRadialFoundation = radialSelection?.type === 'foundation'
        ? selectedRadialNode
        : null;
    const selectedRadialPredicate = radialSelection?.type === 'predicate'
        ? selectedRadialNode
        : null;
    const selectedPredicateThemes = selectedRadialPredicate
        ? radialTree.edges.filter((edge) => edge.source === selectedRadialPredicate.id && edge.claim.editorial && !edge.claim.creedal).map((edge) => radialTree.nodes.find((item) => item.id === edge.target)?.node.label).filter(Boolean)
        : [];
    const selectedPredicateCreeds = selectedRadialPredicate
        ? radialTree.edges.filter((edge) => edge.source === selectedRadialPredicate.id && edge.claim.creedal).map((edge) => radialTree.nodes.find((item) => item.id === edge.target)?.node.label).filter(Boolean)
        : [];
    const selectedRadialEdge = radialSelection?.type === 'dependency'
        ? radialTree.edges.find((edge) => edge.id === radialSelection.id)
        : null;
    const selectedPredicateEdges = selectedRadialEdge
        ? radialTree.edges.filter((edge) => edge.kind === 'depends-on' && edge.claim.id === selectedRadialEdge.claim.id)
        : [];
    const selectedRecordedPredicateEdges = selectedRadialPredicate
        ? radialTree.edges.filter((edge) => edge.target === selectedRadialPredicate.id)
        : [];
    const radialRouteNodes = new Set();
    const radialRouteEdges = new Set();
    const isRouteSelection = radialSelection && radialSelection.type !== 'hub';
    let routeStartIds = selectedRadialPerspective
        ? [selectedRadialPerspective.id]
        : selectedRecordedPredicateEdges.length
            ? [...new Set(selectedRecordedPredicateEdges.map((edge) => edge.source))]
            : selectedPredicateEdges.length ? [...new Set(selectedPredicateEdges.map((edge) => edge.source))] : [];
    if (selectedRadialFoundation) {
        const dependents = new Map();
        radialTree.edges.forEach((edge) => {
            if (!dependents.has(edge.target)) dependents.set(edge.target, []);
            dependents.get(edge.target).push(edge.source);
        });
        const visitedDependents = new Set();
        const queue = [selectedRadialFoundation.id];
        routeStartIds = [];
        while (queue.length) {
            const target = queue.shift();
            if (visitedDependents.has(target)) continue;
            visitedDependents.add(target);
            (dependents.get(target) || []).forEach((source) => {
                if (!visitedDependents.has(source)) {
                    queue.push(source);
                    if (network.nodes.get(source)?.type === 'perspective') routeStartIds.push(source);
                }
            });
        }
        radialRouteNodes.add(selectedRadialFoundation.id);
    }
    if (selectedRadialEdge) {
        const layerById = new Map(radialTree.nodes.map((item) => [item.id, item.layer]));
        const nodeTypeById = new Map(radialTree.nodes.map((item) => [item.id, item.node.type]));
        const incoming = new Map();
        radialTree.edges.forEach((edge) => {
            if (!incoming.has(edge.target)) incoming.set(edge.target, []);
            incoming.get(edge.target).push(edge.source);
        });
        const ancestors = new Set();
        const visitedAncestors = new Set();
        const queue = [selectedRadialEdge.source];
        while (queue.length) {
            const id = queue.shift();
            if (visitedAncestors.has(id)) continue;
            visitedAncestors.add(id);
            if (nodeTypeById.get(id) === 'perspective') ancestors.add(id);
            (incoming.get(id) || []).filter((source) => (layerById.get(source) || 0) > (layerById.get(id) || 0)).forEach((source) => queue.push(source));
        }
        if (!ancestors.size && radialTree.nodes.find((item) => item.id === selectedRadialEdge.source)?.node.type === 'perspective') ancestors.add(selectedRadialEdge.source);
        if (ancestors.size) routeStartIds = [...ancestors];
    }
    if (routeStartIds.length) {
        const layerById = new Map(radialTree.nodes.map((item) => [item.id, item.layer]));
        const route = traceDependencyRoutes(routeStartIds, radialTree.edges, layerById);
        route.nodeIds.forEach((id) => radialRouteNodes.add(id));
        route.edgeIds.forEach((id) => radialRouteEdges.add(id));
    }
    const fitRadialView = () => {
        const viewport = radialViewportRef.current;
        if (!viewport) return .1;
        const available = Math.max(160, Math.min(viewport.clientWidth - 40, viewport.clientHeight - 40));
        return Math.max(.005, Math.min(.8, +(available / Math.max(radialTree.width, radialTree.height)).toFixed(3)));
    };
    useEffect(() => {
        if (viewMode !== 'radial') return undefined;
        const frame = window.requestAnimationFrame(() => setRadialZoom(fitRadialView()));
        return () => window.cancelAnimationFrame(frame);
    }, [viewMode, radialTree.width, radialTree.height]);
    useEffect(() => {
        if (viewMode !== 'radial') return undefined;
        const frame = window.requestAnimationFrame(() => {
            const viewport = radialViewportRef.current;
            if (!viewport) return;
            viewport.scrollLeft = Math.max(0, (viewport.scrollWidth - viewport.clientWidth) / 2);
            viewport.scrollTop = Math.max(0, (viewport.scrollHeight - viewport.clientHeight) / 2);
        });
        return () => window.cancelAnimationFrame(frame);
    }, [viewMode, radialTree.width, radialTree.height, radialZoom]);
    const selected = network.nodes.get(selectedId);
    const selectedConnections = useMemo(() => {
        const ids = new Set(selectedId ? [selectedId] : []);
        const edgeKeys = new Set();
        if (!selectedId) return { ids, edgeKeys };
        branch.edges.forEach((edge) => {
            if (edge.source === selectedId || edge.target === selectedId) {
                ids.add(edge.source);
                ids.add(edge.target);
                edgeKeys.add(`${edge.source}|${edge.target}|${edge.label}`);
            }
        });
        return { ids, edgeKeys };
    }, [selectedId, branch]);
    const searchMatches = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return new Set(needle
            ? branch.nodes.filter((node) => `${node.label} ${node.subtitle}`.toLowerCase().includes(needle)).map((node) => node.id)
            : []);
    }, [query, branch]);
    const needle = query.trim().toLowerCase();
    const filteredPredicates = allPredicateNodes.filter((predicate) => {
        if (!needle) return true;
        const grounded = network.edges
            .filter((edge) => edge.source === predicate.id && edge.label === 'grounds')
            .map((edge) => network.nodes.get(edge.target)?.label || '')
            .join(' ');
        return `${predicate.label} ${grounded}`.toLowerCase().includes(needle);
    });
    const filteredPerspectives = allPerspectiveNodes.filter((node) => {
        if (!needle) return true;
        const connected = network.edges
            .filter((edge) => edge.source === node.id || edge.target === node.id)
            .map((edge) => network.nodes.get(edge.source === node.id ? edge.target : edge.source)?.label || '')
            .join(' ');
        return `${node.label} ${node.subtitle} ${connected}`.toLowerCase().includes(needle);
    });

    const chooseRoot = (id) => {
        setRootId(id);
        setSelectedId(null);
        setQuery('');
    };
    const toggleSelection = (id) => setSelectedId((current) => current === id ? null : id);
    const toggleRadialBadge = (badge) => {
        setRadialBadges((current) => current.includes(badge) ? current.filter((item) => item !== badge) : [...current, badge]);
        setRadialSelection(null);
    };
    const selectRadialNode = (selection) => setRadialSelection((current) =>
        current?.type === selection.type && current.id === selection.id && current.leafId === selection.leafId ? null : selection
    );
    const handleRadialNodeKeyDown = (event, selection) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        selectRadialNode(selection);
    };
    const centerRadialView = () => {
        const viewport = radialViewportRef.current;
        if (!viewport) return;
        viewport.scrollLeft = Math.max(0, (viewport.scrollWidth - viewport.clientWidth) / 2);
        viewport.scrollTop = Math.max(0, (viewport.scrollHeight - viewport.clientHeight) / 2);
    };
    const fitWholeRadialTree = () => {
        setRadialZoom(fitRadialView());
        centerRadialView();
        window.requestAnimationFrame(() => radialViewportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    };
    const edgeKey = (edge) => `${edge.source}|${edge.target}|${edge.label}`;
    const startPan = (event) => {
        if (event.button !== 0 || event.target.closest('.perspective-chart-node, .perspective-radial-node, .perspective-radial-edge, a, button')) return;
        const viewport = event.currentTarget;
        if (!viewport) return;
        panRef.current = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            scrollLeft: viewport.scrollLeft,
            scrollTop: viewport.scrollTop,
        };
        viewport.setPointerCapture(event.pointerId);
        setIsPanning(true);
        event.preventDefault();
    };
    const movePan = (event) => {
        const pan = panRef.current;
        const viewport = event.currentTarget;
        if (!pan || !viewport || pan.pointerId !== event.pointerId) return;
        viewport.scrollLeft = pan.scrollLeft - (event.clientX - pan.x);
        viewport.scrollTop = pan.scrollTop - (event.clientY - pan.y);
    };
    const stopPan = (event) => {
        const viewport = event.currentTarget;
        if (panRef.current?.pointerId !== event.pointerId) return;
        if (viewport?.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
        panRef.current = null;
        setIsPanning(false);
    };
    const startRadialGesture = (event) => {
        if (event.pointerType === 'mouse') {
            startPan(event);
            return;
        }
        if (event.button !== 0 || event.target.closest('.perspective-radial-node, .perspective-radial-edge, a, button')) return;
        const viewport = event.currentTarget;
        const gesture = radialGestureRef.current;
        gesture.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        viewport.setPointerCapture(event.pointerId);
        event.preventDefault();
        if (gesture.pointers.size === 1) {
            panRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, scrollLeft: viewport.scrollLeft, scrollTop: viewport.scrollTop };
            setIsPanning(true);
        } else if (gesture.pointers.size === 2) {
            const [first, second] = [...gesture.pointers.values()];
            gesture.pinch = { distance: Math.hypot(second.x - first.x, second.y - first.y), zoom: radialZoom };
            panRef.current = null;
            setIsPanning(false);
        }
    };
    const moveRadialGesture = (event) => {
        if (event.pointerType === 'mouse') {
            movePan(event);
            return;
        }
        const viewport = event.currentTarget;
        const gesture = radialGestureRef.current;
        if (!gesture.pointers.has(event.pointerId)) return;
        gesture.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (gesture.pointers.size >= 2 && gesture.pinch) {
            const [first, second] = [...gesture.pointers.values()];
            const distance = Math.hypot(second.x - first.x, second.y - first.y);
            if (gesture.pinch.distance > 0) {
                const nextZoom = gesture.pinch.zoom * distance / gesture.pinch.distance;
                setRadialZoom(Math.max(.005, Math.min(.8, +nextZoom.toFixed(3))));
            }
            event.preventDefault();
            return;
        }
        const pan = panRef.current;
        if (!pan || pan.pointerId !== event.pointerId) return;
        viewport.scrollLeft = pan.scrollLeft - (event.clientX - pan.x);
        viewport.scrollTop = pan.scrollTop - (event.clientY - pan.y);
        event.preventDefault();
    };
    const stopRadialGesture = (event) => {
        if (event.pointerType === 'mouse') {
            stopPan(event);
            return;
        }
        const viewport = event.currentTarget;
        const gesture = radialGestureRef.current;
        if (!gesture.pointers.has(event.pointerId)) return;
        gesture.pointers.delete(event.pointerId);
        if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
        gesture.pinch = null;
        const remaining = [...gesture.pointers.entries()][0];
        if (remaining) {
            const [pointerId, point] = remaining;
            panRef.current = { pointerId, x: point.x, y: point.y, scrollLeft: viewport.scrollLeft, scrollTop: viewport.scrollTop };
            setIsPanning(true);
        } else {
            panRef.current = null;
            setIsPanning(false);
        }
    };

    return (
        <main className="perspectives-page perspective-chart-page">
            <SEO canonical="/perspectives/chart" title="Perspective Relationship Map | Bible Perspectives" description="Browse the branching relationships between biblical perspectives and the interpretive predicates they depend on." />
            <div className="perspectives-frame">
                <nav className="perspective-breadcrumb" aria-label="Breadcrumb">
                    <Link to="/perspectives">Perspectives</Link><span aria-hidden="true">/</span><span>Relationship map</span>
                </nav>

                <header className="perspectives-hero chart-hero">
                    <span className="perspectives-hero-strip" aria-hidden="true" />
                    <div className="perspectives-hero-copy">
                        <p className="perspectives-eyebrow">Follow the argument network</p>
                        <h1>Perspectives &amp; predicates</h1>
                        <p>Explore how perspectives connect: follow the relationship map, start from an idea or viewpoint, or fan out from a central belief through predicates to perspectives.</p>
                    </div>
                </header>

                <section className="perspective-chart-mode-picker" aria-label="Choose a browsing style">
                    <p className="perspectives-eyebrow">Choose how to explore</p>
                    <div className="perspective-chart-modes" role="group" aria-label="Chart browsing styles">
                        <button type="button" aria-pressed={viewMode === 'radial'} className={viewMode === 'radial' ? 'is-active' : ''} onClick={() => setViewMode('radial')}>
                            <span aria-hidden="true">✳</span><strong>Radial mind map</strong><small>Filter by badge; branch 360°</small>
                        </button>
                        <button type="button" aria-pressed={viewMode === 'map'} className={viewMode === 'map' ? 'is-active' : ''} onClick={() => setViewMode('map')}>
                            <span aria-hidden="true">◉</span><strong>Relationship map</strong><small>See branches and cross-links</small>
                        </button>
                        <button type="button" aria-pressed={viewMode === 'predicates'} className={viewMode === 'predicates' ? 'is-active' : ''} onClick={() => setViewMode('predicates')}>
                            <span aria-hidden="true">≡</span><strong>Browse by idea</strong><small>Start with a predicate</small>
                        </button>
                        <button type="button" aria-pressed={viewMode === 'perspectives'} className={viewMode === 'perspectives' ? 'is-active' : ''} onClick={() => setViewMode('perspectives')}>
                            <span aria-hidden="true">◫</span><strong>Browse perspectives</strong><small>Start with a viewpoint</small>
                        </button>
                    </div>
                </section>

                {viewMode === 'radial' ? <section className="radial-badge-controls" aria-label="Filter the radial mind map by badge">
                    <div className="radial-badge-search-row">
                        <label className="perspective-chart-search">
                            <span>Search perspectives and predicates</span>
                            <input value={radialSearch} onChange={(event) => { setRadialSearch(event.target.value); setRadialSelection(null); }} placeholder="Titles, summaries, or predicate text" />
                        </label>
                        <div className="radial-filter-actions">
                            <button type="button" className="radial-filter-toggle" aria-expanded={filtersOpen} aria-controls="radial-filter-panel" onClick={() => setFiltersOpen((open) => !open)}>
                                Filters{radialBadges.length ? ` (${radialBadges.length})` : ''}<span aria-hidden="true">⌄</span>
                            </button>
                            <button type="button" className="radial-clear-filters" onClick={() => { setRadialBadges([]); setBadgeQuery(''); setRadialSearch(''); setRadialSelection(null); }}>Clear all</button>
                        </div>
                    </div>
                    {filtersOpen ? <div className="radial-filter-panel" id="radial-filter-panel">
                        <label className="perspective-chart-search">
                            <span>Find a badge</span>
                            <input value={badgeQuery} onChange={(event) => setBadgeQuery(event.target.value)} placeholder="Search the badge pills" />
                        </label>
                        <p className="radial-badge-helper">Combine one or more badge filters. With no filters, the complete graph is shown.</p>
                        <div className="radial-badge-pills" aria-label="Perspective badges">
                            {allPerspectiveBadges.filter((badge) => badge.toLowerCase().includes(badgeQuery.trim().toLowerCase())).map((badge) => <button key={badge} type="button" aria-pressed={radialBadges.includes(badge)} className={radialBadges.includes(badge) ? 'is-active' : ''} onClick={() => toggleRadialBadge(badge)}>{badge}</button>)}
                            {!allPerspectiveBadges.some((badge) => badge.toLowerCase().includes(badgeQuery.trim().toLowerCase())) ? <span className="radial-no-badges">No badges match that search.</span> : null}
                        </div>
                    </div>
                    : null}
                    {radialBadges.length || radialSearch ? <p className="radial-active-filters">Showing {radialTree.selectedCount} matching {radialTree.selectedCount === 1 ? 'perspective' : 'perspectives'}{radialBadges.length ? <> with badges <strong>{radialBadges.join(' · ')}</strong> <span>(match any selected badge)</span></> : null}{radialSearch ? <> matching <strong>“{radialSearch}”</strong></> : null}.</p> : null}
                </section> : null}

                {viewMode !== 'radial' ? <section className="perspective-chart-controls" aria-label="Browse controls">
                    {viewMode === 'map' ? <label className="perspective-chart-root">
                        <span>Start map from a foundational idea</span>
                        <select value={rootId} onChange={(event) => chooseRoot(event.target.value)}>
                            {foundationalPredicates.map((node) => <option key={node.id} value={node.id}>{node.label}</option>)}
                        </select>
                    </label> : null}
                    <label className="perspective-chart-search">
                        <span>{viewMode === 'map' ? 'Find in this branch' : 'Search connected ideas and perspectives'}</span>
                        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Try creation, agency, or wisdom" />
                    </label>
                </section> : null}

                {viewMode === 'map' ? <div className="perspective-chart-summary">
                    <span><strong>{branch.nodes.length}</strong> nodes</span>
                    <span><strong>{branch.edges.length}</strong> relationships</span>
                    <div className="perspective-chart-zoom">
                        <button type="button" onClick={() => setZoom((value) => Math.max(.4, +(value - .1).toFixed(2)))} aria-label="Zoom out">−</button>
                        <span>{Math.round(zoom * 100)}%</span>
                        <button type="button" onClick={() => setZoom((value) => Math.min(1.3, +(value + .1).toFixed(2)))} aria-label="Zoom in">+</button>
                        <button type="button" onClick={() => setZoom(.92)}>Reset</button>
                    </div>
                </div> : null}

                {viewMode === 'map' ? <section className="perspective-chart-shell" aria-label="Relationship map">
                    <div className="perspective-chart-legend">
                        <span className="legend-perspective" /> Perspective
                        <span className="legend-predicate" /> Predicate
                        <span className="legend-line" /> relationship
                    </div>
                    <div
                        ref={chartViewportRef}
                        className={`perspective-chart-scroll ${isPanning ? 'is-panning' : ''}`}
                        onPointerDown={startPan}
                        onPointerMove={movePan}
                        onPointerUp={stopPan}
                        onPointerCancel={stopPan}
                    >
                        <svg
                            className="perspective-chart-svg"
                            viewBox={`0 0 ${branch.width} ${branch.height}`}
                            style={{ width: branch.width * zoom, height: branch.height * zoom }}
                            role="img"
                            aria-label="A branching hierarchy of perspectives and predicates"
                        >
                            <defs>
                                <marker id="chart-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto">
                                    <path d="M0,0 L6,3 L0,6 z" fill="currentColor" />
                                </marker>
                            </defs>
                            <g className="perspective-chart-edges">
                                {branch.edges.map((edge, index) => {
                                    const from = branch.positions.get(edge.source);
                                    const to = branch.positions.get(edge.target);
                                    const highlighted = selectedConnections.edgeKeys.has(edgeKey(edge));
                                    const muted = selectedId && !highlighted;
                                    const isTreeEdge = branch.treeEdges.includes(edge);
                                    const startX = from.x + NODE_WIDTH;
                                    const startY = from.y + NODE_HEIGHT / 2;
                                    const endX = to.x;
                                    const endY = to.y + NODE_HEIGHT / 2;
                                    const bendX = startX + (endX - startX) * .48;
                                    return (
                                        <g key={`${edgeKey(edge)}|${index}`} className={`${highlighted ? 'is-connected' : ''} ${muted ? 'is-dimmed' : ''} ${isTreeEdge ? '' : 'is-cross-link'}`}>
                                            <path d={`M ${startX} ${startY} C ${bendX} ${startY}, ${bendX} ${endY}, ${endX} ${endY}`} markerEnd="url(#chart-arrow)" />
                                            {(highlighted || !selectedId) ? <text x={bendX} y={(startY + endY) / 2 - 6}>{edge.label}</text> : null}
                                        </g>
                                    );
                                })}
                            </g>
                            {branch.nodes.map((node) => {
                                const point = branch.positions.get(node.id);
                                const isSelected = selectedId === node.id;
                                const isConnected = selectedConnections.ids.has(node.id) && !isSelected;
                                const isMuted = selectedId && !selectedConnections.ids.has(node.id);
                                const isSearchMatch = searchMatches.has(node.id);
                                return (
                                    <g
                                        key={node.id}
                                        className={`perspective-chart-node ${node.type} ${isSelected ? 'is-selected' : ''} ${isConnected ? 'is-connected' : ''} ${isMuted ? 'is-dimmed' : ''} ${isSearchMatch ? 'is-search-match' : ''}`}
                                        transform={`translate(${point.x}, ${point.y})`}
                                        onClick={() => toggleSelection(node.id)}
                                        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') toggleSelection(node.id); }}
                                        role="button"
                                        tabIndex="0"
                                        aria-pressed={isSelected}
                                        aria-label={`${node.type}: ${node.label}`}
                                    >
                                        <rect width={NODE_WIDTH} height={NODE_HEIGHT} rx="9" />
                                        <text className="chart-node-label" x="17" y="27">{truncate(node.label, 43)}</text>
                                        <text className="chart-node-subtitle" x="17" y="50">{truncate(node.subtitle, 48)}</text>
                                    </g>
                                );
                            })}
                        </svg>
                    </div>
                </section> : null}

                {viewMode === 'radial' ? <>
                    <div className="perspective-chart-summary radial-chart-summary">
                        <span><strong>{radialTree.selectedCount}</strong> matching perspectives · <strong>{radialTree.perspectiveCount}</strong> perspectives in connected routes</span>
                        <span><strong>{radialTree.layers.length}</strong> concentric rings</span>
                        <span><strong>{radialTree.totalLeaves}</strong> predicate and foundation links</span>
                        <span><strong>{radialTree.predicateNodeCount}</strong> explicit predicate nodes</span>
                        <span><strong>{radialTree.recordedPredicateCount}</strong> recorded predicates without an authored foundation link</span>
                    </div>
                    <section className="perspective-radial-shell" aria-label="Radial perspective dependency map">
                        {radialTree.nodes.length ? <div className={`radial-diagram-workspace${radialSelection ? ' has-selection' : ''}`}>
                            {radialSelection ? <aside className="radial-selection-window radial-selection-drawer" aria-live="polite" aria-label="Selected chart details">
                                <div className="radial-selection-copy">
                                    <p className="perspectives-eyebrow">{selectedRadialPerspective ? 'Selected perspective' : selectedRadialFoundation ? selectedRadialFoundation.creedSources?.length ? 'Creedal root-level predicate' : 'Provisional chart foundation' : selectedRadialPredicate ? 'Recorded predicate commitment' : selectedRadialEdge ? 'Selected depends-on link' : 'Starting belief'}</p>
                                    <h2>{selectedRadialPerspective?.label || selectedRadialFoundation?.label || selectedRadialPredicate?.label || selectedRadialEdge?.claim.label || 'Belief in God'}</h2>
                                    {selectedRadialPerspective ? <>
                                        <p className="radial-selection-category">{selectedRadialPerspective.subtitle}</p>
                                        <p className="radial-selection-text">{selectedRadialPerspective.reading.thesis}</p>
                                        {selectedRadialPerspective.reading.summary ? <p className="radial-selection-text radial-selection-summary">{selectedRadialPerspective.reading.summary}</p> : null}
                                        <p className="radial-selection-category">Ring {radialTree.nodes.find((item) => item.id === selectedRadialPerspective.id)?.layer} · {selectedRadialPerspective.reading.predicates?.length || 0} recorded predicate commitments</p>
                                        {selectedRadialPerspective.reading.predicates?.length ? <ul className="radial-selection-predicate-list">{selectedRadialPerspective.reading.predicates.map((predicate, index) => <li key={`${predicate.canonicalId || predicate.label}-${index}`}><span>{predicate.label}</span><small>{predicate.linked ? 'Linked foundation' : 'Claim recorded; no foundation link assigned'}</small></li>)}</ul> : <p className="radial-selection-summary">No topic-specific predicate links are recorded. Where present, the map’s inferred template foundation is only a browsing scaffold.</p>}
                                    </> : selectedRadialFoundation ? <>
                                        <p className="radial-selection-category">{selectedRadialFoundation.category} · provisional chart foundation</p>
                                        <p className="radial-selection-text">{selectedRadialFoundation.explanation}</p>
                                        {selectedRadialFoundation.creedSources?.length ? <p className="radial-selection-category">Creedal source: {selectedRadialFoundation.creedSources.join(' · ')}</p> : null}
                                        {selectedRadialFoundation.creedSourceUrl ? <p><a className="perspectives-topic-link" href={selectedRadialFoundation.creedSourceUrl} target="_blank" rel="noreferrer">Read the creed texts <span aria-hidden="true">↗</span></a></p> : null}
                                        {selectedRadialFoundation.references ? <p className="radial-selection-category">Biblical reference points: {selectedRadialFoundation.references}</p> : null}
                                        <p className="radial-selection-category">{radialTree.edges.filter((edge) => edge.target === selectedRadialFoundation.id).length} direct depends-on links · selecting this foundation highlights every descendant route</p>
                                    </> : selectedRadialPredicate ? <>
                                        <p className="radial-selection-category">Explicit predicate commitment · {selectedRadialPredicate.targetId ? 'linked to a foundation reading' : 'no narrower parent is recorded'}</p>
                                        <p className="radial-selection-text">{selectedRadialPredicate.explanation}</p>
                                        <p className="radial-selection-category">Used by {selectedRecordedPredicateEdges.length} perspective {selectedRecordedPredicateEdges.length === 1 ? 'reading' : 'readings'} · {selectedRadialPredicate.targetIds?.length ? `linked foundation${selectedRadialPredicate.targetIds.length === 1 ? '' : 's'}: ${selectedRadialPredicate.targetIds.map((id) => radialTree.nodes.find((item) => item.id === id)?.node.label).filter(Boolean).join('; ')}.` : selectedPredicateThemes.length || selectedPredicateCreeds.length ? `no authored foundation link; ${selectedPredicateThemes.length ? `editorial theme${selectedPredicateThemes.length === 1 ? '' : 's'}: ${selectedPredicateThemes.join('; ')}. ` : ''}${selectedPredicateCreeds.length ? `creedal framework: ${selectedPredicateCreeds.join('; ')}.` : ''}` : 'no deeper foundation link is recorded.'} {selectedRadialPredicate.targetIds?.length && selectedPredicateCreeds.length ? `Editorial creedal framework: ${selectedPredicateCreeds.join('; ')}.` : ''}</p>
                                        <div className="radial-selection-related">{[...new Set(selectedRecordedPredicateEdges.map((edge) => edge.source))].map((id) => network.nodes.get(id)).filter(Boolean).map((node) => <Link key={`${selectedRadialPredicate.id}-${node.id}`} to={node.href}>{node.label}</Link>)}</div>
                                    </> : selectedRadialEdge ? <>
                                        <p className="radial-selection-category">{selectedRadialEdge.claim.directWordingMatch ? 'Editorial creed link · direct wording match, not authored' : selectedRadialEdge.claim.creedal ? 'Editorial creed framework · not authored' : selectedRadialEdge.claim.editorial ? 'Editorial theme association · inferred from wording, not authored' : selectedRadialEdge.kind === 'grounds' ? 'Predicate-to-foundation link' : 'Depends-on predicate link'}{selectedRadialEdge.claim.editorial ? '' : ` · used by ${new Set(selectedPredicateEdges.map((edge) => edge.source)).size} perspectives`}</p>
                                        <p className="radial-selection-text">All matching commitments and their full dependency routes are highlighted. This link connects <strong>{radialTree.nodes.find((item) => item.id === selectedRadialEdge.source)?.node.label}</strong> to <strong>{radialTree.nodes.find((item) => item.id === selectedRadialEdge.target)?.node.label}</strong>.</p>
                                        <div className="radial-selection-related">{[...new Set(selectedPredicateEdges.map((edge) => edge.source))].map((id) => <span key={id}>{network.nodes.get(id)?.label}</span>)}</div>
                                        {selectedRadialEdge.claim.argument ? <p className="radial-selection-text radial-selection-summary">{selectedRadialEdge.claim.argument}</p> : null}
                                    </> : <p className="radial-selection-text">This map compares Christian perspectives, so belief in God is the shared starting premise. Provisional foundations trace broad commitments about creation, revelation, and goodness into Scripture, human dignity, and neighbour-love, before branching into clear-boundary, contextual, and pastoral/social approaches. Explicit predicates without a verified deeper parent remain visible as root commitments. Search a phrase or select badges to focus on a manageable branch.</p>}
                                </div>
                                <div className="radial-selection-actions">
                                    {selectedRadialPerspective ? <Link className="perspectives-topic-link" to={selectedRadialPerspective.href}>Open full perspective <span aria-hidden="true">→</span></Link> : null}
                                    {selectedRadialEdge ? <>{network.nodes.get(selectedRadialEdge.source)?.href ? <Link className="perspectives-topic-link" to={network.nodes.get(selectedRadialEdge.source).href}>Open dependent perspective <span aria-hidden="true">→</span></Link> : null}{network.nodes.get(selectedRadialEdge.target)?.href ? <Link className="perspectives-topic-link" to={network.nodes.get(selectedRadialEdge.target).href}>Open foundation <span aria-hidden="true">→</span></Link> : null}</> : null}
                                    <button type="button" className="radial-selection-close" onClick={() => setRadialSelection(null)}>Close</button>
                                </div>
                            </aside> : null}
                            <div className="radial-viewport-frame">
                            <div
                            ref={radialViewportRef}
                            className={`perspective-radial-viewport ${isPanning ? 'is-panning' : ''}`}
                            onPointerDown={startRadialGesture}
                            onPointerMove={moveRadialGesture}
                            onPointerUp={stopRadialGesture}
                            onPointerCancel={stopRadialGesture}
                        >
                            <svg className="perspective-radial-svg" viewBox={`0 0 ${radialTree.width} ${radialTree.height}`} style={{ width: radialTree.width * radialZoom, height: radialTree.height * radialZoom }} role="group" aria-label={`Radial map with ${radialTree.layers.length} concentric rings, ${radialTree.perspectiveCount} perspectives and ${radialTree.totalLeaves} predicate and foundation links`} onClick={(event) => {
                                if (event.target.closest('.perspective-radial-node, .perspective-radial-edge')) return;
                                setRadialSelection(null);
                            }}>
                                <g className="radial-layer-guides">{radialTree.layers.filter((layer) => layer.radius > 0).map((layer) => <circle key={layer.index} cx={radialTree.center} cy={radialTree.center} r={layer.radius} />)}</g>
                                <g className="radial-edges">{radialTree.nodes.filter((item) => item.layer === 1).map((item) => {
                                    const dx = item.position.x - radialTree.center; const dy = item.position.y - radialTree.center;
                                    const distance = Math.hypot(dx, dy) || 1;
                                    const start = { x: radialTree.center + dx / distance * 112, y: radialTree.center + dy / distance * 112 };
                                    const end = { x: item.position.x - dx / distance * 145, y: item.position.y - dy / distance * 145 };
                                    const control = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
                                    const connected = radialRouteNodes.has(item.id);
                                    const dimmed = isRouteSelection && !connected;
                                    return <path key={`hub-${item.id}`} className={`radial-hub-edge${connected ? ' is-connected' : dimmed ? ' is-dimmed' : ''}`} d={`M ${start.x} ${start.y} Q ${control.x} ${control.y} ${end.x} ${end.y}`} />;
                                })}{radialTree.edges.map((edge) => {
                                    const source = radialTree.nodes.find((item) => item.id === edge.source);
                                    const target = radialTree.nodes.find((item) => item.id === edge.target);
                                    if (!source || !target) return null;
                                    const dx = target.position.x - source.position.x; const dy = target.position.y - source.position.y;
                                    const distance = Math.hypot(dx, dy) || 1;
                                    const offset = (amount) => ({ x: dx / distance * amount, y: dy / distance * amount });
                                    const startOffset = offset(145); const endOffset = offset(145);
                                    const start = { x: source.position.x + startOffset.x, y: source.position.y + startOffset.y };
                                    const end = { x: target.position.x - endOffset.x, y: target.position.y - endOffset.y };
                                    const sourceRadius = radialTree.layers[source.layer].radius;
                                    const targetRadius = radialTree.layers[target.layer].radius;
                                    const radialGap = sourceRadius - targetRadius;
                                    const controlStartRadius = sourceRadius - radialGap * .34;
                                    const controlEndRadius = targetRadius + radialGap * .34;
                                    const controlStart = { x: radialTree.center + Math.cos(source.angle) * controlStartRadius, y: radialTree.center + Math.sin(source.angle) * controlStartRadius };
                                    const controlEnd = { x: radialTree.center + Math.cos(target.angle) * controlEndRadius, y: radialTree.center + Math.sin(target.angle) * controlEndRadius };
                                    const angleDelta = Math.atan2(Math.sin(target.angle - source.angle), Math.cos(target.angle - source.angle));
                                    const cycleControlAngle = source.angle + angleDelta / 2;
                                    const cycleControl = { x: radialTree.center + Math.cos(cycleControlAngle) * sourceRadius * .94, y: radialTree.center + Math.sin(cycleControlAngle) * sourceRadius * .94 };
                                    const path = source.layer === target.layer
                                        ? `M ${start.x} ${start.y} Q ${cycleControl.x} ${cycleControl.y} ${end.x} ${end.y}`
                                        : `M ${start.x} ${start.y} C ${controlStart.x} ${controlStart.y} ${controlEnd.x} ${controlEnd.y} ${end.x} ${end.y}`;
                                    const selected = radialSelection?.type === 'dependency' && radialSelection.id === edge.id;
                                    const connected = radialRouteEdges.has(edge.id);
                                    const dimmed = isRouteSelection && !selected && !connected;
                                    const relationshipPhrase = edge.claim.directWordingMatch ? 'has wording related to' : edge.claim.creedal ? 'is linked by the editorial framework to' : edge.claim.editorial ? 'is provisionally grouped under' : edge.kind === 'grounds' ? 'grounds' : 'depends on';
                                    return <path key={edge.id} className={`perspective-radial-edge${edge.kind === 'grounds' ? ' is-grounding' : ''}${edge.claim.editorial ? ' is-editorial' : ''}${source.layer <= target.layer ? ' is-cycle-crosslink' : ''}${selected ? ' is-selected' : connected ? ' is-connected' : dimmed ? ' is-dimmed' : ''}`} d={path}><title>{`${source.node.label} ${relationshipPhrase} ${target.node.label}: ${edge.claim.label}`}</title></path>;
                                })}</g>
                                {radialTree.nodes.map((item) => {
                                    const { node, position, layer } = item;
                                    const lines = wrapSvgLabel(node.label, 31, 2);
                                    const selected = (radialSelection?.type === 'perspective' || radialSelection?.type === 'foundation') && radialSelection.id === node.id;
                                    const connected = radialRouteNodes.has(node.id);
                                    const dimmed = isRouteSelection && !selected && !connected;
                                    const nodeType = node.type === 'foundation' ? 'foundation' : node.type === 'predicate' ? 'predicate' : 'perspective';
                                    const className = `perspective-radial-node ${nodeType}${selected || radialSelection?.type === 'foundation' && radialSelection.id === node.id ? ' is-selected' : connected ? ' is-connected' : dimmed ? ' is-dimmed' : ''}`;
                                    const selection = { type: nodeType, id: node.id };
                                    return <g className={className} key={node.id} transform={`translate(${position.x},${position.y})`} role="button" tabIndex={0} aria-pressed={selected} aria-label={`Ring ${layer}, ${nodeType === 'foundation' ? 'inferred foundation' : nodeType === 'predicate' ? 'recorded predicate without a linked foundation' : 'perspective'}: ${node.label}`} onClick={() => selectRadialNode(selection)} onKeyDown={(event) => handleRadialNodeKeyDown(event, selection)}>
                                        <title>{`${node.label} — ${node.subtitle} · ring ${layer}`}</title>
                                        <rect x="-138" y="-49" width="276" height="98" rx="13" />
                                        <text className="radial-node-kicker" x="0" y="-27">LAYER {layer} · {nodeType === 'foundation' ? node.category === 'First principles' ? 'FIRST PRINCIPLE' : node.category === 'Theological foundations' ? 'THEOLOGICAL FOUNDATION' : node.category === 'Thematic foundations' ? 'EDITORIAL THEME' : node.category === 'Creedal foundations' ? 'CREEDAL FOUNDATION' : 'APPROACH FOUNDATION' : nodeType === 'predicate' ? node.targetIds?.length ? 'RECORDED PREDICATE' : radialTree.edges.some((edge) => edge.source === node.id && edge.claim.editorial) ? 'RECORDED PREDICATE · EDITORIAL ROUTE' : 'ROOT PREDICATE · NO VERIFIED PARENT' : 'PERSPECTIVE'}</text>
                                        <text className="radial-node-label" x="0" y={lines.length === 1 ? 2 : -6}>{lines.map((line, index) => <tspan key={`${node.id}-${index}`} x="0" dy={index === 0 ? 0 : 18}>{line}</tspan>)}</text>
                                        <text className="radial-node-subtitle" x="0" y="34">{truncate(node.subtitle, 34)}</text>
                                    </g>;
                                })}
                                <g className={`perspective-radial-node hub${radialSelection?.type === 'hub' || routeStartIds.length ? ' is-selected' : ''}`} transform={`translate(${radialTree.center},${radialTree.center})`} role="button" tabIndex={0} aria-pressed={radialSelection?.type === 'hub'} aria-label="Select foundational predicate: Belief in God" onClick={() => selectRadialNode({ type: 'hub', id: 'hub' })} onKeyDown={(event) => handleRadialNodeKeyDown(event, { type: 'hub', id: 'hub' })}>
                                    <circle r="108" />
                                    <text className="radial-hub-kicker" x="0" y="-18">FOUNDATIONAL PREDICATE</text>
                                    <text className="radial-hub-label" x="0" y="11">Belief in God</text>
                                    <text className="radial-hub-note" x="0" y="38">follow foundations outward</text>
                                </g>
                            </svg>
                            </div>
                            <div className="perspective-chart-zoom radial-viewport-zoom">
                                <button type="button" onClick={() => setRadialZoom((value) => Math.max(.005, +(value / 1.35).toFixed(3)))} aria-label="Zoom out radial mind map">−</button>
                                <span>{Math.round(radialZoom * 100)}%</span>
                                <button type="button" onClick={() => setRadialZoom((value) => Math.min(.8, +(value * 1.35).toFixed(3)))} aria-label="Zoom in radial mind map">+</button>
                                <button type="button" onClick={fitWholeRadialTree}>Fit whole tree</button>
                            </div>
                            </div>
                        </div> : <div className="perspective-chart-empty"><h2>No perspectives matched</h2><p>Try another search phrase or clear a badge filter.</p></div>}
                        <p className="perspective-radial-note">The centre is the shared starting premise, “belief in God.” Apostles’ and Nicene-Constantinopolitan affirmations form the first creedal ring around it; broad creation, revelation, and goodness principles branch beyond that ring. Each recorded predicate occurrence has its own node, placed in the ring directly inward from the perspective that records it, so repeated wording stays attached to its specific reading. Linked foundations continue inward where the dependency structure allows. Dotted links from predicates to creed clauses are editorial: a direct wording match can connect to a creed clause, while broader unlinked claims can branch through one or more topical themes. These are proposed analytical browsing routes, not claims that a perspective explicitly cites a creed. A claim opposed to a creedal statement is not shown as depending on it. Nicene Son-language remains distinct from the Apostles’ Creed. Rings show radial layout position, not a ranking of beliefs; cycles and other cross-links remain visible across rings.</p>
                        <div className="perspective-radial-legend"><span className="radial-legend-hub" /> Shared starting premise <span className="radial-legend-predicate" /> Editorial foundation <span className="radial-legend-perspective" /> Perspective <span className="radial-legend-unlinked" /> Recorded predicate <span className="radial-legend-edge" /> Depends on · <span className="radial-legend-grounding" /> Grounds foundation · <span className="radial-legend-editorial" /> Inferred theme / creedal bridge · <span className="radial-legend-cycle" /> Cycle cross-link retained</div>
                        <div className="radial-layer-legend" aria-label="Concentric ring counts">{radialTree.layers.map((layer) => <span key={layer.index}><i>{layer.index}</i> {layer.type === 'starting-premise' ? 'shared starting premise' : `${layer.count} ${layer.count === 1 ? 'node' : 'nodes'}`}</span>)}</div>
                    </section>
                    <p className="perspective-chart-hint">The whole map opens fitted to the chart. Zoom in to read node labels. Select a perspective, predicate, or relationship to highlight every inward dependency branch toward its foundations; other recorded cross-links remain visible.</p>
                </> : null}

                {viewMode === 'predicates' ? (
                    <section className="perspective-browse-list" aria-label="Ideas and the perspectives they ground">
                        <div className="perspective-browse-intro"><span>{filteredPredicates.length} ideas</span><p>Open an idea to see perspectives that use it as a foundation. Select a perspective to read its full explanation.</p></div>
                        {filteredPredicates.map((predicate) => {
                            const grounded = network.edges.filter((edge) => edge.source === predicate.id && edge.label === 'grounds').map((edge) => network.nodes.get(edge.target)).filter(Boolean);
                            const related = network.edges.filter((edge) => edge.source === predicate.id && edge.label !== 'grounds').map((edge) => network.nodes.get(edge.target)).filter((node) => node?.type === 'perspective');
                            return <details className="perspective-browse-group" key={predicate.id}>
                                <summary><span className="browse-type-tag">Idea</span><span className="browse-summary-title">{predicate.label}</span><span className="browse-count">{grounded.length || related.length} perspectives</span></summary>
                                <div className="perspective-browse-items">{[...grounded, ...related].filter((node, index, items) => items.findIndex((item) => item.id === node.id) === index).map((node) => <Link className="perspective-browse-item" key={`${predicate.id}-${node.id}`} to={node.href}><span>{node.label}</span><small>{node.subtitle}</small><b aria-hidden="true">→</b></Link>)}{!grounded.length && !related.length ? <p className="browse-no-links">No linked perspectives are recorded yet.</p> : null}</div>
                            </details>;
                        })}
                        {!filteredPredicates.length ? <p className="perspective-chart-empty">No ideas matched that search.</p> : null}
                    </section>
                ) : null}

                {viewMode === 'perspectives' ? (
                    <section className="perspective-explorer-grid" aria-label="Perspectives and their connections">
                        <p className="perspective-browse-intro perspective-browse-intro-wide"><span>{filteredPerspectives.length} perspectives</span><span>Each card shows the commitments and nearby perspectives linked to it.</span></p>
                        {filteredPerspectives.map((node) => {
                            const links = network.edges.filter((edge) => edge.source === node.id || edge.target === node.id).map((edge) => ({ edge, node: network.nodes.get(edge.source === node.id ? edge.target : edge.source) })).filter((item) => item.node);
                            const predicates = links.filter((item) => item.node.type === 'predicate');
                            const perspectivesNearby = links.filter((item) => item.node.type === 'perspective').slice(0, 3);
                            return <article className="perspective-explorer-card" key={node.id}>
                                <p className="perspectives-topic-category">{node.subtitle}</p><h2>{node.label}</h2>
                                <div className="explorer-card-section"><strong>Connected ideas</strong><div className="explorer-chip-list">{predicates.length ? predicates.map((item, index) => <span className="explorer-chip" key={`${item.node.id}-${index}`}>{item.node.label}</span>) : <span className="explorer-muted">No linked ideas recorded</span>}</div></div>
                                {perspectivesNearby.length ? <div className="explorer-card-section"><strong>Nearby perspectives</strong><div className="explorer-neighbors">{perspectivesNearby.map((item, index) => <Link key={`${item.node.id}-${index}`} to={item.node.href}>{item.node.label}</Link>)}</div></div> : null}
                                <Link className="perspectives-topic-link" to={node.href}>Open perspective <span aria-hidden="true">→</span></Link>
                            </article>;
                        })}
                        {!filteredPerspectives.length ? <p className="perspective-chart-empty">No perspectives matched that search.</p> : null}
                    </section>
                ) : null}

                {viewMode === 'map' ? selected ? (
                    <aside className={`perspective-chart-detail ${selected.type}`} aria-live="polite">
                        <div>
                            <p className="perspectives-eyebrow">Selected {selected.type}</p>
                            <h2>{selected.label}</h2>
                            <p>{selected.type === 'perspective' ? selected.reading.thesis : 'An interpretive commitment supporting a connected perspective.'}</p>
                        </div>
                        <div className="perspective-chart-detail-actions">
                            {selected.type === 'predicate' && selected.id !== rootId ? <button type="button" onClick={() => chooseRoot(selected.id)}>Start from here</button> : null}
                            {selected.type === 'perspective' ? <Link className="perspectives-topic-link" to={selected.href}>Open perspective <span aria-hidden="true">→</span></Link> : selected.topicSlug ? <Link className="perspectives-topic-link" to={`/perspectives/${selected.topicSlug}#${selected.readingId}`}>Open grounded reading <span aria-hidden="true">→</span></Link> : null}
                            <button type="button" className="perspective-chart-clear" onClick={() => setSelectedId(null)}>Clear</button>
                        </div>
                    </aside>
                ) : <p className="perspective-chart-hint">Drag the chart background to move around. Select a node to highlight its immediate connections.</p> : null}
            </div>
        </main>
    );
}

export default PagePerspectiveChart;
